import { ColorModeContext, useMode } from "./theme";
import { Backdrop, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import Dashboard from "./components/dashboard/Dashboard";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Order from "./components/operation/orders/Order";
import Form from "./components/administrator/Form";
import SignIn from "./components/signin/SignIn";
import ShadowboxSignIn from "./components/shadowbox/ShadowboxSignIn";
import PharmacyShipToCoverage from "./components/operation/pharmacyCoverage/PharmacyShipToCoverage";

import {
  checkAuthentication,
  reNewAccessToken,
  resetAuthState,
  selectIsValueFoundInToken,
  updateUserInfo,
  useAuthSlice,
} from "./redux/slice/authSlice";
import { MyProSidebarProvider } from "./components/common/sidebar/sidebarContext";
import PracticeRegistration from "./components/operation/practice/PracticeRegistration";
import PracticeList from "./components/operation/practice/PracticeList";
import CreatePractice from "./components/operation/practice/CreatePractice";
import Page404 from "./components/common/Page404";
import PracticeRequest from "./components/operation/practice/PracticeRequest";
import PracticeEditView from "./components/operation/practice/PracticeEditView";
import ProductList from "./components/operation/product/ProductList";
import Product from "./components/operation/product/Product";
import Success from "./components/common/Success";
import PatientsList from "./components/operation/patients/PatientsList";
import CreatePatient from "./components/operation/patients/CreatePatient";
import ProductAgreement from "./components/operation/ProductAgreement/ProductAgreement";
import CreateProductAgreement from "./components/operation/ProductAgreement/CreateProductAgreement";
import { useEffect, useLayoutEffect, useState } from "react";
import { fetchAllCodeTablesValues } from "./redux/slice/codetableSlice";
import { useAppDispatch } from "./redux/slice/redux-hooks";
import PurchaseAgreement from "./components/operation/ProductAgreement/PurchaseAgreement";
import CreateNewAgreement from "./components/operation/ProductAgreement/CreateNewAgreement";
import { fetchAppConfigList, setSkipLoadingDialog, showHideLoadingDialog, useCommonReducer } from "./redux/slice/commonSlice";
import CustomErrorAlert from "./components/common/CustomErrorAlert";
import CustomSingleAlert from "./components/common/CustomSingleAlert";
import UserList from "./components/operation/user/UserList";
import CreateUser from "./components/operation/user/CreateUser";
import IVRList from "./components/operation/ivr/IVRList";
import { isFinanceHqSalesSheetRoute, setDispatch, setNavigate, setSkipGlobalLoader } from "./service/http";
import LoadingDialog from "./components/common/LoadingDialog";
import PublicRoute from "./components/navigate/PublicRoute";
import ProtectedRoute from "./components/navigate/ProtectedRoute";
import IVRForm from "./components/operation/ivr/IVRForm";
import OrderList from "./components/operation/orders/OrderList";
import BenefitSummary from "./components/operation/ivr/BenefitSummary";
import TissueTracking from "./components/operation/orders/TissueTracking";
import Returns from "./components/operation/orders/Returns";
import Details from "./components/operation/orders/Details";
import OutsidePlatformOrderDetails from "./components/operation/orders/OutsidePlatformOrderDetails";
import {
  getDataFromCookies,
  getDataFromLocalStorage,
  removeItemFromLocalStorage,
  setDataToCookies,
  setDataToLocalStorage,
} from "./utils/StorageUtils";
import ProductAgreementSign from "./components/operation/ProductAgreement/ProductAgreementSign";
import IVRDetails from "./components/operation/ivr/IVRDetails";
import ConsignmentOrder from "./components/operation/orders/ConsignmentOrder";
import IVRNote from "./components/operation/ivr/IVRNote";
import OrderFulfillment from "./components/operation/orders/OrderFulfillment";
import ReSignIn from "./components/signin/ReSignIn";
import { fetchAppVersion, useUserReducer } from "./redux/slice/userSlice";
import Footer from "./components/common/Footer";
import AgencyList from "./components/operation/agency/AgencyList";
import AgencyForm from "./components/operation/agency/AgencyForm";
import InvoiceList from "./components/operation/invoice/InvoiceList";
import Invoice from "./components/operation/invoice/Invoice";
import SignUp2FA from "./components/2FA/SignUp2FA";
import Retrieve2FA from "./components/2FA/Retrieve2FA";
import Billing from "./components/operation/orders/Billing";
import ForgotPassword from "./components/signin/ForgotPassword";
import ResetPassword from "./components/signin/ResetPassword";
import ChangePassword from "./components/operation/user/ChangePassword";
import useToken from "./hooks/useToken";
import AccountProfile from "./components/dashboard/AccountProfile";
import { fetchAllUserTwoFactorList } from "./redux/slice/2FA/twoFactorSlice";
import { incrementInstanceCount, setupWindowCloseHandler } from "./utils/WindowUtils";
import CacheBuster, { useCacheBuster } from "react-cache-buster";
import CommPayment from "./components/operation/invoice/CommPayement";
import RecordPayout from "./components/operation/invoice/RecordPayout";
import BillingDetail from "./components/operation/invoice/BillingDetail";
import RecordPayoutOverride from "./components/operation/invoice/RecordPayoutOverride";
import ConfigList from "./components/operation/configuration/ConfigList";
import BaaList from "./components/operation/baa/BaaList";
import BaaAgreement from "./components/operation/baa/BaaAgreement";
import ManufactureNotificationAccess from "./components/operation/manufacturerNotification/ManufacturerNotificationAccess";
import ManufacturerNotificationSignUp from "./components/operation/manufacturerNotification/ManufacturerNotificationSignUp";
import InvoiceSharingAccess from "./components/operation/invoice/InvoiceSharingAccess";
import AuditLogList from "./components/operation/auditLog/AuditLogList";
import AiAuditList from "./components/operation/aiMedicalAudit/AiAuditList";
import AiAuditDetails from "./components/operation/aiMedicalAudit/AiAuditDetails";
import MedicalNoteAuditPollingManager from "./components/operation/aiMedicalAudit/MedicalNoteAuditPollingManager";
import MedicalNoteAuditCompletionNotification from "./components/operation/aiMedicalAudit/MedicalNoteAuditCompletionNotification";
import ManufacturerList from "./components/operation/manufacturerNotification/ManufacturerList";
import CreateMaufacturer from "./components/operation/manufacturerNotification/CreateManufacturer";
import { decryptAES, encrypt } from "./utils/EncryptorUtils";
import useTokenWorker from "./hooks/useTokenWorker";
import ProductsList from "./components/operation/product/ProductsList";
import RecordCreditMemo from "./components/operation/invoice/RecordCreditMemo";
import ServiceSubscriptionList from "./components/operation/serviceSubscription/ServiceSubscriptionList";
import AddPaymentDetail from "./components/operation/invoice/AddPaymentDetail";
import EditInvoice from "./components/operation/invoice/EditInvoice";
import JobSchedulerList from "./components/operation/jobScheduler/JobSchedulerList";
import Ec2ServerControl from "./components/dashboard/Ec2ServerControl"; // CS-2018
import { updateTokenToCookie } from "./utils/Utility";
import DocumentList from "./components/operation/document/DocumentList";
import ExternalLink from "./components/operation/document/ExternalLink";
import OptionsPopup from "./components/common/OptionsPopup";
import UserPermissions from "./components/operation/user/UserPermissions";
import CreateNewAgreementNew from "./components/operation/ProductAgreement/CreateNewAgreementNew";
import TemplateList from "./components/operation/templates/TemplateList";
import TemplateForm from "./components/operation/templates/TemplateForm";
import ProviderList from "./components/operation/provider/ProviderList";
import AncillaryOrderForm from "./components/operation/orders/AncillaryOrderForm";
import PatientDetails from "./components/operation/patients/PatientDetails";
import ReleaseNotePopup from "./components/common/ReleaseNotePopup";
import ErrorBoundary from "./ErrorBoundary";
import ErrorPage from "./ErrorPage";
import { NavigationContextProvider, useNavigation } from "./hooks/useNavigation";
import AccessRestrictedComponent from "./components/common/AccessRestrictedComponent";
import { getDocumentCategoryList } from "./redux/slice/document/DocumentSlice";
import DocumentAccessPopup from "./components/operation/document/DocumentAccessPopup";
import TrackingLoading from "./components/common/TrackingLoading";
import InboxList from "./components/operation/inbox/InboxList";
import TissueTrackingForMultipleOrders from "./components/operation/orders/TissueTrackingForMultipleOrders";
import PaymentList from "./components/operation/payment/PaymentList";
import { fetchImportantNotifications, notificationAttachmentDTO, useCreateNotificationSlice } from "./redux/slice/inbox/createNotificationSlice";
import ImportantMessagePopup from "./components/common/ImportantMessagePopup";
import ResetPasswordOnLogin from "./components/signin/ResetPasswordOnLogin";
import RecordPayment from "./components/operation/payment/RecordPayment";
import { CAN_ACCESS_APPLICATION, SHIFT_KEY, USER_ROLES } from "./constants/applicationConstants";
import PaymentDetails from "./components/operation/payment/PaymentDetails";
import ClaimList from "./components/operation/claim/ClaimList";
import NotificationList from "./components/operation/notification/NotificationList";
import { useNotifications } from "./hooks/useNotifications";
import { useScrollToTopOnNavigate } from "./hooks/useScrollToTopOnNavigate";
import RecordClaim from "./components/operation/claim/RecordClaim";
import ProviderNotificationAccess from "./components/operation/provider/ProviderNotificationAccess";
import PrivacyPolicy from "./components/publicAccess/PrivacyPolicy";
import Terms2fa from "./components/publicAccess/Terms2fa";
import StripeConnectOAuthAccess from "./components/operation/payment/stripe/StripeConnectOAuthAccess";
import ClaimDetails from "./components/operation/claim/ClaimDetails";
import ProviderTemplateDetails from "./components/operation/provider/ProviderTemplateDetails";
import { useSelector } from "react-redux";
import { RootState } from "./redux/store";
import CreateInvoice from "./components/operation/invoice/CreateInvoice";
import CommissionList from "./components/operation/commission/CommissionList";
import FinanceHqSalesSheetPage from "./components/operation/commission/FinanceHqSalesSheetPage";
import CommissionDetails from "./components/operation/commission/CommissionDetails";
import MigrateDetails from "./components/operation/orders/MigrateDetails";
import ActivityCenter from "./components/operation/activity/ActivityCenter";
import MigrateBillingDetail from "./components/operation/invoice/MigrateBillingDetail";
import ManageReturnRequest from "./components/operation/orders/ManageReturnRequest";
import StripePaymentVerifySession from "./components/operation/payment/stripe/StripePaymentVerifySession";
import PracticeDetailsView from "./components/operation/practice/practiceDetailsView";
import CreateAccountStep from "./components/operation/account/CreateAccountStep";
import SetupTwoFactor from "./components/operation/account/SetupTwoFactor";
import VerifyTwoFactor from "./components/operation/account/VerifyTwoFactor";
import PlatformTourStep from "./components/operation/account/PlatformTourStep";
import SelectCareCategories from "./components/operation/account/SelectCareCategories";
import ExploreMarket from "./components/operation/exploreMarket/ExploreMarket";
import RequestAgreementList from "./components/operation/exploreMarket/RequestAgreementList";
import AgreementRequestAccess from "./components/operation/exploreMarket/AgreementRequestAccess";
import IVRBillerAccess from "./components/operation/ivr/IVRBillerAccess";
import FaceSheetList from "./components/operation/faceSheet/FaceSheetList";
import IsrManagement from "./components/operation/isr/IsrManagement";
import PrescriptionList from "./components/operation/prescription/PrescriptionList";
import PastOrder from "./components/operation/orders/PastOrder";

function App() {
  const [theme, colorMode] = useMode();

  let location = useLocation();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const isTokenValueAvailable = useToken();
  useTokenWorker();

  const [reLoginPopupShow, setReLoginPopupShow] = useState(false);
  const [versionPeriodically, setVersionPeriodically] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState<{ message: string, show: boolean }>({ message: "", show: false });
  const [showReleasePopup, setShowReleasePopup] = useState<{ version: string, show: boolean }>({ version: "", show: false });
  const [showExternalLinkPopup, setShowExternalLinkPopup] = useState(false);
  const { ImportantNotificationInfo } = useCreateNotificationSlice();
  const locationFromHook = useLocation();
  // const [importantMsgPopup, setImportantMsgPopup] = useState<{
  // show: boolean;
  // messages: { notificationId: number; subject: string; message: string, time: string, attachments: notificationAttachmentDTO[], 
  //   notificationType: string, targetEntityId: number
  //  }[];
  //     }>({
  //       show: false,
  //       messages: []
  //     });
  const canAccessNotifications = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.INBOX_MANAGEMENT)(state));


  let checkAuthen: boolean = false;

  let goto: string | null = getDataFromLocalStorage("GoTo");
  let givenUserEmail: string = ""

  const {
    showLoadingDialog,
    loadingDialogConfig,
    showMainErrorPopup,
    mainErrorObj,
    showSingleAlert,
    singleAlertObj,
    showTrackingLoadingDialog
  } = useCommonReducer();

  const { userInfo, isValid } = useAuthSlice();

  const { appVersionFromBackend } = useUserReducer();

  setDispatch(dispatch);
  setNavigate(navigate);
  const suppressGlobalLoader = isFinanceHqSalesSheetRoute(location.pathname);
  useLayoutEffect(() => {
    setSkipGlobalLoader(suppressGlobalLoader);
    dispatch(setSkipLoadingDialog(suppressGlobalLoader));
  }, [suppressGlobalLoader, dispatch]);
  useNotifications();
  useScrollToTopOnNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location?.pathname === "/shadowbox" && location?.search) {
      updateTokenToCookie(params.get("token"))
      setDataToCookies("formData", params.get("formData") || "", { sameSite: 'None', secure: true });
    }
    const encryptedEmail = params.get('email') || null;
    const iv = params.get('iv');
    const salt = params.get('salt');
    if (encryptedEmail != null && encryptedEmail !== '') {
      givenUserEmail = decryptAES(encryptedEmail, iv, salt);
    }
    checkAuthen = false;
    removeItemFromLocalStorage("GoTo");
    checkAuth(givenUserEmail);
    dispatch(fetchAllCodeTablesValues({ skipErrorPopup: true }));

    const storedVersion = localStorage.getItem("applicationVersion");
    if (storedVersion == null) {
      dispatch(fetchAppConfigList());
    }
    setupWindowCloseHandler();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "logout" || event.key === "login") {
        dispatch(resetAuthState());
        if (window.location.pathname !== "/signIn") {
          navigate("/signIn");
        }
      }
    };
    if (location?.pathname === "/externalLink") {
      setShowExternalLinkPopup(true);
      dispatch(getDocumentCategoryList(true));

    }
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };

  }, []);

  useEffect(() => {
    if (isValid) {
      checkVersion();
    }
  }, [location, userInfo.applicationVersion, isValid]);

  const checkVersion = () => {
    const storedVersion = localStorage.getItem("applicationVersion");
    if (storedVersion == null || storedVersion !== userInfo.applicationVersion) {
      setShowReleasePopup({
        version: userInfo.applicationVersion,
        show: true,
      });
    }
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkVersionPeriodically();
    }, 1 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);


  useEffect(() => {
    if (userInfo.refreshTokenExpired) setReLoginPopupShow(true);
  }, [userInfo.refreshTokenExpired]);

  const checkVersionPeriodically = async () => {
    try {
      const response = await dispatch(
        fetchAppVersion({ skipLoader: true })
      );

      const backendVersion = response.payload.applicationVersion;
      const releaseNote = response.payload.releaseNote;
      const storedVersion = localStorage.getItem("applicationVersion");

      if (releaseNote !== null) {
        dispatch(updateUserInfo({ releaseNote: releaseNote }));
      }

      setVersionPeriodically(true);

      if (storedVersion == null || storedVersion !== backendVersion) {
        setShowReleasePopup({
          version: backendVersion,
          show: true,
        });
      }
    } catch (error) {
      console.error("Error during periodic version check:", error);
    }
  };


  const checkAuth = async (givenUserEmail: string) => {
    try {
      let response = await dispatch(checkAuthentication({ interceptorOption: { skipErrorPopup: true }, givenUserEmail }));
      if (
        response.payload &&
        response.payload.code === "V3OK" &&
        response.payload.message === "User get successfully" &&
        (!response.payload.errors || response.payload.errors.length === 0)
      ) {

        const storedVersion = localStorage.getItem("applicationVersion");
        if (storedVersion == null || storedVersion !== response.payload.data.applicationVersion) {
          setShowReleasePopup({
            version: response.payload.data.applicationVersion,
            show: true,
          });
        }

        checkAuthen = true;
        removeItemFromLocalStorage("GoTo");
        dispatch(fetchAllUserTwoFactorList(false));
      } else {
        checkAuthen = false;
        setGotoPageAfterLogin();
      }
    } catch (error) {
      setGotoPageAfterLogin();
      checkAuthen = false;
    }
  };

  const setGotoPageAfterLogin = async () => {
    let externalPathsToAllow: string[] = ["/sign_product_agreement"];
    let externalSearch = location.search;
    let pathname = location.pathname;
    if (
      externalSearch &&
      externalSearch.trim().length > 0 &&
      pathname &&
      pathname.trim().length > 0 &&
      externalPathsToAllow.includes(pathname)
    ) {
      setDataToLocalStorage("GoTo", pathname + externalSearch);
    }
  };

  const { checkCacheStatus } = useCacheBuster();

  const handleCheckVersion = async () => {
    try {
      const isNewVersion = await checkCacheStatus();
      if (isNewVersion) {
        onShowPopup();
      } else {
        console.log('You are using the latest version.');
      }
    } catch (error) {
      console.error('Error checking for new version:', error);
    }
  };

  const onCloseWithReloadPopup = () => {

    if (versionPeriodically) {
      localStorage.setItem("applicationVersion", appVersionFromBackend.applicationVersion);
    } else {
      localStorage.setItem("applicationVersion", userInfo.applicationVersion);
    }
    window.location.reload();
    window.location.href = window.location.href;
    setShowReleasePopup({ version: "", show: false })
  }

  const onShowPopup = () => {
    setShowInfoPopup({ message: "Need a browser refresh for the application to work properly ", show: true })
  }

  useEffect(() => {
    handleCheckVersion();
  }, [location]);

  const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const authCookieValue = getDataFromCookies(encrypt(`2FA${userInfo?.userId}CHECK`, SHIFT_KEY))

  useEffect(() => {

    if (userInfo.notificationEnable) {
      if (!userInfo?.securityTokens?.hasOwnProperty('enable2FA')) {
        if (!userInfo.passwordResetRequired && userInfo.fromSignedInPage
          && locationFromHook.pathname === '/signIn') {
          dispatch(fetchImportantNotifications());
        }
      } else {
        if (userInfo.fromSignedInPage
          && isValid && location.state
          && (locationFromHook.pathname === '/signIn' || locationFromHook.pathname === '/retrive2fa')
          && authCookieValue != null && authCookieValue != '' && authCookieValue.length > 0
        ) {
          dispatch(fetchImportantNotifications());
        }
      }
    }
  }, [isValid, authCookieValue, userInfo])

  //   useEffect(()=>{
  //   if (canAccessNotifications && userInfo.notificationEnable && ImportantNotificationInfo && ImportantNotificationInfo?.length > 0) {
  //     const messages = ImportantNotificationInfo.map((info: any) => ({
  //       subject: info.notificationSub || "<No Subject>",
  //       message: stripHtml(info.notificationMsg) || "",
  //       notificationId: info.notificationId,
  //       time: info.auditTimeStamp,
  //       attachments: info.notificationAttachment.length > 0 ? info.notificationAttachment : [],
  //       notificationType: info.notificationType,
  //       targetEntityId: info.targetEntityId
  //     }));

  //     setImportantMsgPopup({
  //       show: true,
  //       messages
  //     });
  //   }

  // },[ImportantNotificationInfo])

  return (
    <CacheBuster
      currentVersion={userInfo.applicationVersion}
      isEnabled={true}
      isVerboseMode={false}
      loadingComponent={<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true} >
        <CircularProgress size={60} variant="indeterminate" />
      </Backdrop>}
      metaFileDirectory={'.'}
    >
      <ColorModeContext.Provider value={colorMode}>
        <NavigationContextProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute fallback="/">
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ivr"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <IVRForm />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Order />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ancillaryorder"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AncillaryOrderForm />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* <Route
                path="/past_order"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PastOrder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              /> */}
              <Route
                path="/fulfill_order"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <OrderFulfillment />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <OrderList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/claim_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ClaimList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prescription_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PrescriptionList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/record_claim"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <RecordClaim />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/form"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Form />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/setting"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AccountProfile />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PracticeList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_practice"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreatePractice />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice_request"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PracticeRequest />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice_edit_view"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PracticeEditView />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* CS-1406 - New Practice Details Page */}
              <Route
                path="/practiceDetails"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PracticeDetailsView />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProductList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/explore_market"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ExploreMarket />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/agreement_request_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <RequestAgreementList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProductsList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/template_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TemplateList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/template"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TemplateForm />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Product />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/isr"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <IsrManagement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PatientsList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_patient"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreatePatient />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editPatient"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreatePatient />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patientDetails"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PatientDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice_product_agreement"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProductAgreement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_practice_product_agreement"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreateProductAgreement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sign_product_agreement"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProductAgreementSign />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase_agreement"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PurchaseAgreement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_new_agreement"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreateNewAgreementNew />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <UserList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user_create"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreateUser />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change_password"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ChangePassword />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ivr_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <IVRList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/benefit_summary"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <BenefitSummary />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tissue_tracking"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TissueTracking />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/manage_return"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ManageReturnRequest />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/returns"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Returns />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/details/:id"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Details />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/outside_platform_order_details/:id"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <OutsidePlatformOrderDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/migrate_details/:id"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <MigrateDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Billing />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_invoice"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreateInvoice />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/claim_details"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ClaimDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ivr_details"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <IVRDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ivr_note"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <IVRNote />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_consignment_order"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ConsignmentOrder />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AgencyList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency_form"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AgencyForm />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <InvoiceList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/edit_invoice"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <EditInvoice />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/invoice"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Invoice />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stripePayment_verify_session"
                element={
                  <ProtectedRoute>
                    <StripePaymentVerifySession />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ConfigList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* CS-518 */}
              <Route
                path="/auditlog"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AuditLogList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/*  */}
              {/* AI Medical Audit */}
              <Route
                path="/ai_medical_audit"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AiAuditList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/*  */}
              {/* AI Medical Audit Details*/}
              <Route
                path="/ai_audit_details"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AiAuditDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/*  CS - 1790  */}
              {/* FaceSheet Extraction Tool List */}
              <Route
                path="/facesheet"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <FaceSheetList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/*  */}
              {/* CS-554 */}
              <Route
                path="/manufacturer"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ManufacturerList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manufacturer_create"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CreateMaufacturer />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/*  */}
              <Route
                path="/comm_payment"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CommPayment />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recordPayout"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <RecordPayout />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/overrideRecordPayout"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <RecordPayoutOverride />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing_detail"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <BillingDetail />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/migrate_billing_detail"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <MigrateBillingDetail />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/record_payment"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AddPaymentDetail />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/record_credit_memo"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <RecordCreditMemo />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accountProfile"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AccountProfile />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* CS-1302 - Start */}
              <Route
                path="/providerNotificationAccess"
                element={
                  <PublicRoute fallback={"/providerNotificationAccess"}>
                    <ErrorBoundary>
                      <ProviderNotificationAccess />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              {/*  */}
              <Route
                path="/provider_templateDetails"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProviderTemplateDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manufactureNotificationAccess"
                element={
                  <PublicRoute fallback={"/manufactureNotificationAccess"}>
                    <ErrorBoundary>
                      <ManufactureNotificationAccess />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/invoiceSharingAccess"
                element={
                  <PublicRoute fallback={"/invoiceSharingAccess"}>
                    <ErrorBoundary>
                      <InvoiceSharingAccess />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/ivrBillerAccess"
                element={
                  <PublicRoute fallback={"/ivrBillerAccess"}>
                    <ErrorBoundary>
                      <IVRBillerAccess />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/agreementRequestAccess"
                element={
                  <PublicRoute fallback={"/agreementRequestAccess"}>
                    <ErrorBoundary>
                      <AgreementRequestAccess />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/manufacturerNotificationSignUp"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <ManufacturerNotificationSignUp />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/signup2fa"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <SignUp2FA />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                // CS-1852
                path="/resource_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <DocumentList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user_permissions"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <UserPermissions />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/retrive2fa"
                element={
                  <PublicRoute fallback={"/retrive2fa"}>
                    <ErrorBoundary>
                      <Retrieve2FA />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/signIn"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <SignIn />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/shadowbox"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <ShadowboxSignIn />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="*"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <Page404 />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/success"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <Success />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <ForgotPassword />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/account/reset/:code"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <ResetPassword />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <ResetPassword />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/account/reset-password"
                element={
                  <PublicRoute fallback={"/account/reset-password"}>
                    <ErrorBoundary>
                      <ResetPasswordOnLogin />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/externalLink"
                element={
                  <PublicRoute fallback={"/externalLink"}>
                    <ErrorBoundary>
                      <ExternalLink />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/practice"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PracticeRegistration />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/baa_agreement"
                element={
                  <ProtectedRoute fallback={"baa_agreement"}>
                    <ErrorBoundary>
                      <BaaAgreement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/baa_detail"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <BaaList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service_subscription_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ServiceSubscriptionList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path='/manufacturer_list'
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ManufacturerList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path='/provider_list'
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ProviderList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/error"
                element={
                  <ProtectedRoute>
                    <ErrorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/restricted"
                element={
                  <ProtectedRoute>
                    <AccessRestrictedComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inbox_list"
                element={
                  <ProtectedRoute>
                    <InboxList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notification_list"
                element={
                  <ProtectedRoute>
                    <NotificationList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/multiple_tissue_tracking"
                element={
                  <ProtectedRoute>
                    <TissueTrackingForMultipleOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/record_multiple_payment"
                element={
                  <ProtectedRoute>
                    <RecordPayment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PaymentList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment_detail"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PaymentDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/privacy.html"
                element={
                  <PublicRoute>
                    <PrivacyPolicy />
                  </PublicRoute>
                }
              />
              <Route
                path="/terms-2fa.html"
                element={
                  <PublicRoute>
                    <Terms2fa />
                  </PublicRoute>
                }
              />
              <Route
                path="/stripeConnectOAuthAccess"
                element={
                  <PublicRoute>
                    <StripeConnectOAuthAccess />
                  </PublicRoute>
                }
              />
              <Route
                path="/commission_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CommissionList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance_hq_sales_sheet"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <FinanceHqSalesSheetPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activity_center"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ActivityCenter />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/commission_details"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <CommissionDetails />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent_list"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <JobSchedulerList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* CS-2018 - Super Admin EC2 server dashboard */}
              <Route
                path="/server_management"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Ec2ServerControl />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacy_coverage"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <PharmacyShipToCoverage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create_account_step"
                element={
                  <PublicRoute>
                    <CreateAccountStep />
                  </PublicRoute>
                }
              />
              <Route
                path="/setup_two_factor"
                element={
                  <PublicRoute>
                    <SetupTwoFactor />
                  </PublicRoute>
                }
              />
               <Route
                path="/verify_two_factor"
                element={
                  <PublicRoute>
                    <VerifyTwoFactor />
                  </PublicRoute>
                }
              />
              <Route
                path="/platform_tour"
                element={
                  <PublicRoute>
                    <PlatformTourStep />
                  </PublicRoute>
                }
              />
               <Route
                path="/select_care_categories"
                element={
                  <PublicRoute>
                    <SelectCareCategories />
                  </PublicRoute>
                }
              />
            </Routes>
            {/* </NotificationsProvider> */}
            <Footer />

            {showMainErrorPopup && (
              <CustomErrorAlert
                open={showMainErrorPopup}
                mainErrorObj={mainErrorObj}
                severity={mainErrorObj?.popupSeverity ? mainErrorObj?.popupSeverity : "error"}
              />
            )}

            {showSingleAlert && (
              <CustomSingleAlert
                open={showSingleAlert}
                singleAlertObj={singleAlertObj}
              />
            )}
            <ReSignIn
              open={reLoginPopupShow}
              handleClose={() => setReLoginPopupShow(false)}
            />
            {showReleasePopup.show &&
              <ReleaseNotePopup
                open={showReleasePopup.show}
                onSubmit={onCloseWithReloadPopup}
                version={showReleasePopup.version}
              />
            }
            {/* {userInfo.notificationEnable && importantMsgPopup.show && 
              <ImportantMessagePopup
                open={importantMsgPopup.show}
                messages={importantMsgPopup.messages}
                onClose={() => setImportantMsgPopup({ ...importantMsgPopup, show: false })}
              />
            } */}

            {showLoadingDialog && !suppressGlobalLoader && <LoadingDialog open={showLoadingDialog} config={loadingDialogConfig} />}
            {showTrackingLoadingDialog && <TrackingLoading open={showLoadingDialog} />}
            {showExternalLinkPopup && <DocumentAccessPopup open={showExternalLinkPopup} onClose={() => setShowExternalLinkPopup(false)} />}


            {/* CS-1773 - Run Medical Note Audit AI Tool in Background - start */}
            <MedicalNoteAuditPollingManager />

            <MedicalNoteAuditCompletionNotification />
            {/* CS-1773 - Run Medical Note Audit AI Tool in Background - end */}

          </ThemeProvider>
        </NavigationContextProvider>
      </ColorModeContext.Provider >
    </CacheBuster >
  );
}

export default App;
